import { Box, Flex, HStack, Icon } from "@chakra-ui/react";
import CDS from "design-system";
import { Link as ReactRouterLink } from "react-router";
import { TbCalendar, TbChevronRight } from "react-icons/tb";
import { LuClock, LuCircleAlert } from "react-icons/lu";
import type { NeedsActionItem } from "./helpers";

interface NeedsActionBlockProps {
  items: NeedsActionItem[];
  onIssueClick?: () => void;
}

const ICON_MAP = {
  overdue: { icon: LuClock, color: "red.500", bg: "red.50" },
  meetings: { icon: TbCalendar, color: "orange.500", bg: "orange.50" },
  issues: { icon: LuCircleAlert, color: "orange.500", bg: "orange.50" },
};

function ActionRow({ item, onClick }: { item: NeedsActionItem; onClick?: () => void }) {
  const iconConfig = ICON_MAP[item.kind];

  const content = (
    <Flex
      align="center"
      justify="space-between"
      px="4"
      py="3"
      borderBottom="1px solid"
      borderColor="gray.50"
      _hover={{ bg: "gray.50" }}
      cursor="pointer"
      transition="background 0.15s"
      onClick={onClick}
    >
      <HStack spacing="3" flex={1} minW={0}>
        <Flex align="center" justify="center" w="32px" h="32px" borderRadius="full" bg={iconConfig.bg} flexShrink={0}>
          <Icon as={iconConfig.icon} boxSize="4" color={iconConfig.color} />
        </Flex>
        <Box flex={1} minW={0}>
          <CDS.Text12 fontWeight="600" color="content.primary" noOfLines={1}>
            {item.text}
          </CDS.Text12>
          {item.subtitle && (
            <CDS.Text10 color="content.tertiary" noOfLines={1}>
              {item.subtitle}
            </CDS.Text10>
          )}
        </Box>
      </HStack>
      <Icon as={TbChevronRight} boxSize="4" color="content.tertiary" flexShrink={0} ml="2" />
    </Flex>
  );

  if (onClick) return content;

  return <ReactRouterLink to={item.linkTo}>{content}</ReactRouterLink>;
}

export default function NeedsActionBlock({ items, onIssueClick }: NeedsActionBlockProps) {
  if (items.length === 0) return null;

  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
      <Flex align="center" justify="space-between" px="4" py="2.5" borderBottom="1px solid" borderColor="gray.100">
        <HStack spacing="2">
          <Box w="8px" h="8px" borderRadius="full" bg="red.500" flexShrink={0} />
          <CDS.Text12 fontWeight="600" color="content.primary">
            Needs action
          </CDS.Text12>
        </HStack>
        <CDS.Text10 color="content.tertiary">
          {items.length} item{items.length !== 1 ? "s" : ""}
        </CDS.Text10>
      </Flex>

      {items.map((item, index) => (
        <ActionRow
          key={`${item.kind}-${index}`}
          item={item}
          onClick={item.kind === "issues" ? onIssueClick : undefined}
        />
      ))}
    </Box>
  );
}
